import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Package } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import apiClient from '../../api/client';

/**
 * Part thumbnail, resolved down a preference chain:
 *
 *   1. the PART's own photo   — what the operator actually wants to see
 *   2. the BRAND logo         — a legible second best, so a row is never anonymous
 *   3. `fallback`             — the screen's own placeholder (the orders page draws a part)
 *
 * A photo of the VEHICLE the part fits used to sit between the two. It is gone: it was only ever
 * standing in while we held no part photos, and once the media package was mounted a car next to a
 * brake pad just read as the wrong product.
 *
 * Each step is tried only when the one before it is absent OR fails to load, which is why both
 * URLs travel from the API rather than the server picking one: a part photo that 404s at render
 * time (an external manufacturer host, a media file we don't hold) must fall to the logo, not
 * straight past it to the placeholder.
 *
 * Two kinds of source arrive and they cannot be loaded the same way:
 *   • an absolute `http(s)://…` link (a manufacturer's own image) → a plain <img> is fine;
 *   • a relative `/catalog/media/…` path → that is OUR api, session-gated because the images are
 *     licensed TecDoc assets. An <img> cannot send the bearer token, so it is fetched through
 *     apiClient as a blob and shown via an object URL.
 *
 * Blob URLs are cached per src for the life of the tab: a result list re-renders constantly and one
 * brand logo repeats on every row of the same make, so refetching per render would be waste.
 * Failures are cached too, but only briefly — see the negative cache below.
 */
const blobCache = new Map<string, string>();

/**
 * Negative cache, deliberately TIME-BOUNDED.
 *
 * Most parts have no photo, so retrying a known-missing image on every render is pure waste — but
 * remembering the failure for the whole tab means one transient 404 (a backend restart, a media
 * directory only just configured) leaves every thumbnail stuck on its placeholder until the user
 * happens to reload. Forgetting after a minute keeps the cheap path cheap and lets the page heal.
 */
const FAILURE_TTL_MS = 60_000;
const failedAt = new Map<string, number>();
const isFailed = (src: string): boolean => {
  const at = failedAt.get(src);
  if (at === undefined) return false;
  if (Date.now() - at < FAILURE_TTL_MS) return true;
  failedAt.delete(src);
  return false;
};

export function ArticleImage({
  src,
  brandSrc,
  alt,
  className = '',
  size = 40,
  fallback,
}: {
  /** The part's own photo. */
  src?: string | null;
  /** The brand logo, used only if the part photo is missing or fails to load. */
  brandSrc?: string | null;
  alt: string;
  className?: string;
  size?: number;
  fallback?: ReactNode;
}) {
  const { t } = useTranslation();
  // Ordered candidates; a step already known to be failing is skipped outright.
  const candidates = useMemo(
    () => [src, brandSrc].filter((u): u is string => !!u && !isFailed(u)),
    [src, brandSrc]
  );
  const key = candidates.join('|');

  const [step, setStep] = useState(0);
  const [url, setUrl] = useState<string | null>(null);
  const reqId = useRef(0);

  // A different row (or a healed candidate) restarts the chain from the top.
  useEffect(() => {
    setStep(0);
    setUrl(null);
  }, [key]);

  const current: string | undefined = candidates[step];
  const exhausted = step >= candidates.length;

  useEffect(() => {
    const id = ++reqId.current;
    if (!current) {
      setUrl(null);
      return;
    }
    if (current.startsWith('http')) {
      setUrl(current); // <img> reports failure via onError, which advances the chain
      return;
    }
    const cached = blobCache.get(current);
    if (cached) {
      setUrl(cached);
      return;
    }
    let alive = true;
    setUrl(null);
    (async () => {
      try {
        const res = await apiClient.get<Blob>(current, { responseType: 'blob' });
        if (!alive || id !== reqId.current) return;
        const objectUrl = URL.createObjectURL(res.data);
        blobCache.set(current, objectUrl);
        setUrl(objectUrl);
      } catch {
        if (!alive || id !== reqId.current) return;
        failedAt.set(current, Date.now());
        setStep((s) => s + 1); // fall to the next candidate
      }
    })();
    return () => {
      alive = false;
    };
  }, [current]);

  if (exhausted || (!current && candidates.length === 0)) {
    if (fallback !== undefined) return <>{fallback}</>;
    return (
      <div
        style={{ width: size, height: size }}
        className={`flex shrink-0 items-center justify-center rounded border bg-muted/40 text-muted-foreground ${className}`}
        title={t('ui.noImage')}
        aria-label={t('ui.noImage')}
      >
        <Package size={Math.round(size * 0.5)} />
      </div>
    );
  }

  // Nothing to paint yet (blob in flight) — hold the space so the row doesn't jump.
  if (!url)
    return <div style={{ width: size, height: size }} className={`shrink-0 ${className}`} />;

  // Say when a substitute is on screen rather than passing the logo off as the part.
  const showingBrand = !!brandSrc && current === brandSrc;
  return (
    <img
      src={url}
      alt={alt}
      style={{ maxWidth: size, maxHeight: size }}
      loading="lazy"
      onError={() => {
        if (current) failedAt.set(current, Date.now());
        setStep((s) => s + 1);
      }}
      title={showingBrand ? t('ui.brandLogoTitle', { alt }) : alt}
      className={`shrink-0 object-contain ${showingBrand ? 'opacity-80' : ''} ${className}`}
    />
  );
}
